import { createColumnHelper } from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import { Calendar, CheckCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useApproveManualPayments } from '~entities/payment/api/approve-manual-payments';
import { useGetPaymentDetails } from '~entities/payment/api/get-payment-details';
import type { PaymentDetailsInvoice } from '~entities/payment/modal/invoice';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';

import { EditPendingPayment } from './edit-pending-payment';

interface ViewUpcomingChargesProps {
  date: string;
}
const columnHelper = createColumnHelper<PaymentDetailsInvoice>();

export const ApprovePendingPayment = ({ date }: ViewUpcomingChargesProps) => {
  const parsedDate = parse(date, 'EEE, MMM d, yyyy', new Date());
  const formattedDate = format(parsedDate, 'yyyy-MM-dd');

  const [selectedInvoices, setSelectedInvoices] = useState<Array<string>>([]);

  const { data, isFetching } = useGetPaymentDetails({
    date: formattedDate,
  });

  const mutation = useApproveManualPayments();
  const pendingInvoices = useMemo(
    () =>
      data?.invoices.filter((invoice) => invoice.status === 'pending') ?? [],
    [data?.invoices],
  );

  const handleApprove = async (invoices: Array<string>) => {
    try {
      await mutation.mutateAsync({
        invoiceId: invoices,
        date: formattedDate,
      });
      toast.success('Payments approved successfully');
      setSelectedInvoices([]);
    } catch (error) {
      console.error('Error approving payments:', error);
      toast.error('Failed to approve payments');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedInvoices(pendingInvoices.map((invoice) => invoice.invoiceId));
    } else {
      setSelectedInvoices([]);
    }
  };
  const columns = [
    columnHelper.display({
      id: 'select',
      header: () => (
        <Checkbox
          aria-label='Select all pending payments'
          checked={
            pendingInvoices.length > 0 &&
            selectedInvoices.length === pendingInvoices.length
          }
          onCheckedChange={handleSelectAll}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          aria-label={`Select ${row.original.studentName}`}
          checked={selectedInvoices.includes(row.original.invoiceId)}
          onCheckedChange={(checked) => {
            setSelectedInvoices((current) =>
              checked
                ? [...current, row.original.invoiceId]
                : current.filter((id) => id !== row.original.invoiceId),
            );
          }}
        />
      ),
    }),
    columnHelper.accessor('studentName', { header: 'Student name' }),
    columnHelper.accessor('productName', { header: 'Class / product' }),
    columnHelper.accessor('amount', { header: 'Amount' }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className='flex gap-2'>
          <Button
            variant='outline'
            onClick={() => {
              void handleApprove([row.original.invoiceId]);
            }}
          >
            <CheckCircle className='size-4' />
            Approve
          </Button>
          <EditPendingPayment
            invoiceId={row.original.invoiceId}
            date={date}
            studentName={row.original.studentName}
            productName={row.original.productName}
          />
        </div>
      ),
    }),
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant='outline' className=''>
          <Calendar className='size-4' />
          Approve
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-4xl'>
        <DialogHeader>
          <DialogTitle>Review Pending Payments</DialogTitle>
          <DialogDescription>
            Review and approve payments for {date}
          </DialogDescription>
        </DialogHeader>
        <EnrollioTable
          totalLabel='Pending payments'
          filterHeader={(table) => (
            <EnrollioTableSearch
              table={table}
              placeholder='Search payments...'
            />
          )}
          data={pendingInvoices}
          columns={columns}
          getRowId={(row) => row.invoiceId}
          isUpdating={isFetching || mutation.isPending}
          emptyContent='No pending payments.'
          ariaLabel='Pending payments'
        />
        <DialogFooter>
          <Button className='' onClick={() => handleApprove(selectedInvoices)}>
            Approve Selected
          </Button>
          <DialogClose asChild>
            <Button type='button' variant='outline'>
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
